import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { FolderOpen, FileText, Image as ImageIcon, Download, Trash2, Search, ChevronLeft, ChevronRight, Repeat, Loader2, User, Upload, Users, History, Send, Inbox, Pencil, X } from 'lucide-react';

const ADMIN_ROLES = ['admin', 'owner', 'manager', 'superadmin'];
const AUTHORIZABLE_ROLES = ['vendor', 'guard'];

const AUDIT_EVENT_TYPES = [
  { value: 'file_uploaded', label: 'Subida' },
  { value: 'file_deleted', label: 'Borrado' },
  { value: 'vendor_authorized', label: 'Autorizó vendedor' },
  { value: 'vendor_unauthorized', label: 'Quitó vendedor' },
  { value: 'file_renamed', label: 'Renombrado' },
];

function fileTypeLabel(mime, name) {
  if ((mime || '').startsWith('image/')) return 'Imagen';
  const ext = (name || '').split('.').pop();
  return ext ? ext.toUpperCase() : 'Archivo';
}

function daysUntil(iso) {
  const diff = Math.ceil((new Date(iso) - new Date()) / 86400000);
  if (diff < 0) return 'vencido';
  if (diff === 0) return 'hoy';
  return `${diff} día${diff !== 1 ? 's' : ''}`;
}

function UploadFilePreview({ file, onClear }) {
  const Icon = iconFor(file.type);
  return (
    <div className="flex items-center gap-3 text-left">
      <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
        <Icon className="w-4 h-4 text-primary" />
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-sm text-white truncate">{file.name}</p>
        <p className="text-[11px] text-muted-foreground">{formatFileSize(file.size)}</p>
      </div>
      <Button
        variant="ghost"
        size="sm"
        onClick={(e) => { e.stopPropagation(); onClear(); }}
        className="h-7 w-7 p-0 text-muted-foreground hover:text-destructive shrink-0"
        data-testid="upload-file-clear"
      >
        <X className="w-3.5 h-3.5" />
      </Button>
    </div>
  );
}

function VendorChecklist({ vendors, selected, onToggle, onToggleAll }) {
  const allSelected = vendors.length > 0 && vendors.every(v => selected.has(v.id));
  return (
    <div className="space-y-2">
      <label className="flex items-center gap-2 p-2 rounded-lg bg-black/20 border border-white/5 cursor-pointer">
        <Checkbox checked={allSelected} onCheckedChange={() => onToggleAll(!allSelected)} data-testid="vendor-select-all" />
        <span className="text-xs font-semibold text-white">Seleccionar todos</span>
      </label>
      <div className="max-h-52 overflow-y-auto space-y-1">
        {vendors.length === 0 ? (
          <p className="text-xs text-muted-foreground text-center py-3">No hay vendedores/guardias en el equipo.</p>
        ) : vendors.map(v => (
          <label key={v.id} className="flex items-center gap-2 p-2 rounded-lg hover:bg-white/5 cursor-pointer" data-testid={`vendor-check-${v.id}`}>
            <Checkbox checked={selected.has(v.id)} onCheckedChange={() => onToggle(v.id)} />
            <span className="text-xs text-white">{v.name}</span>
            <span className="text-[9px] text-muted-foreground ml-auto">{v.role === 'guard' ? 'Guardia' : 'Vendedor'}</span>
          </label>
        ))}
      </div>
    </div>
  );
}

function AuditLogModal({ open, onClose, team }) {
  const [events, setEvents] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(0);
  const [loading, setLoading] = useState(false);
  const [types, setTypes] = useState(new Set());
  const [actorId, setActorId] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [search, setSearch] = useState('');

  const load = useCallback(() => {
    if (!open) return;
    setLoading(true);
    api.get('/file-library/audit-log', {
      params: {
        page, limit: 25,
        event_type: Array.from(types).join(','),
        actor_user_id: actorId,
        date_from: dateFrom,
        date_to: dateTo,
        search,
      },
    })
      .then(r => { setEvents(r.data.events); setTotal(r.data.total); setPages(r.data.pages); })
      .catch(() => toast.error('Error al cargar el historial'))
      .finally(() => setLoading(false));
  }, [open, page, types, actorId, dateFrom, dateTo, search]);

  useEffect(() => { load(); }, [load]);

  const toggleType = (v) => {
    setPage(1);
    setTypes(prev => {
      const next = new Set(prev);
      next.has(v) ? next.delete(v) : next.add(v);
      return next;
    });
  };

  const detailFor = (ev) => {
    if (ev.event_type === 'file_uploaded') {
      const n = (ev.details?.authorized_vendor_ids || []).length;
      return `${n} autorizado${n !== 1 ? 's' : ''} inicial${n !== 1 ? 'es' : ''}`;
    }
    if (ev.event_type === 'vendor_authorized' || ev.event_type === 'vendor_unauthorized') {
      return ev.details?.target_vendor_name || '-';
    }
    if (ev.event_type === 'file_renamed') {
      return ev.details?.previous_name ? `antes: ${ev.details.previous_name}` : '-';
    }
    return '-';
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto bg-[#1F2833] border-white/10" data-testid="audit-log-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-white flex items-center gap-2">
            <History className="w-5 h-5 text-primary" /> Historial de actividad
          </DialogTitle>
          <DialogDescription className="text-muted-foreground text-xs">
            Quién subió, borró o autorizó archivos de la Biblioteca — incluye archivos ya borrados.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="flex flex-wrap gap-1.5">
            {AUDIT_EVENT_TYPES.map(t => (
              <button
                key={t.value}
                onClick={() => toggleType(t.value)}
                className={`text-[10px] px-2.5 py-1 rounded-full border transition-colors ${
                  types.has(t.value) ? 'bg-primary/20 border-primary/40 text-primary' : 'border-white/10 text-muted-foreground hover:bg-white/5'
                }`}
                data-testid={`audit-filter-${t.value}`}
              >
                {t.label}
              </button>
            ))}
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            <Select value={actorId || 'all'} onValueChange={(v) => { setPage(1); setActorId(v === 'all' ? '' : v); }}>
              <SelectTrigger className="bg-black/20 border-white/10 text-xs h-8" data-testid="audit-filter-actor">
                <SelectValue placeholder="Usuario" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos los usuarios</SelectItem>
                {team.map(m => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <Input type="date" value={dateFrom} onChange={e => { setPage(1); setDateFrom(e.target.value); }} className="bg-black/20 border-white/10 text-xs h-8" data-testid="audit-filter-from" />
            <Input type="date" value={dateTo} onChange={e => { setPage(1); setDateTo(e.target.value); }} className="bg-black/20 border-white/10 text-xs h-8" data-testid="audit-filter-to" />
            <Input placeholder="Buscar archivo..." value={search} onChange={e => { setPage(1); setSearch(e.target.value); }} className="bg-black/20 border-white/10 text-xs h-8" data-testid="audit-filter-search" />
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-10"><Loader2 className="w-5 h-5 animate-spin text-muted-foreground" /></div>
          ) : events.length === 0 ? (
            <p className="text-xs text-muted-foreground text-center py-8">Sin actividad para estos filtros.</p>
          ) : (
            <div className="overflow-x-auto rounded-lg border border-white/5">
              <table className="w-full text-xs min-w-[600px]">
                <thead>
                  <tr className="border-b border-white/10 text-muted-foreground text-[10px] uppercase tracking-wider">
                    <th className="text-left py-2 px-2.5 font-medium">Evento</th>
                    <th className="text-left py-2 px-2.5 font-medium">Archivo</th>
                    <th className="text-left py-2 px-2.5 font-medium">Quién</th>
                    <th className="text-left py-2 px-2.5 font-medium">Detalle</th>
                    <th className="text-left py-2 px-2.5 font-medium">Fecha</th>
                  </tr>
                </thead>
                <tbody>
                  {events.map((ev, i) => (
                    <tr key={i} className="border-b border-white/5 last:border-0" data-testid="audit-log-row">
                      <td className="py-2 px-2.5 text-white">{AUDIT_EVENT_TYPES.find(t => t.value === ev.event_type)?.label || ev.event_type}</td>
                      <td className="py-2 px-2.5 text-muted-foreground truncate max-w-[160px]" title={ev.file_name}>{ev.file_name}</td>
                      <td className="py-2 px-2.5 text-muted-foreground">{ev.actor_user_name}</td>
                      <td className="py-2 px-2.5 text-muted-foreground">{detailFor(ev)}</td>
                      <td className="py-2 px-2.5 text-muted-foreground whitespace-nowrap">{new Date(ev.timestamp).toLocaleString('es-MX')}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {pages > 1 && (
            <div className="flex items-center justify-center gap-3">
              <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)} className="border-white/10">
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <span className="text-xs text-muted-foreground">Página {page} de {pages} · {total} eventos</span>
              <Button variant="outline" size="sm" disabled={page >= pages} onClick={() => setPage(p => p + 1)} className="border-white/10">
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function formatFileSize(bytes) {
  if (!bytes) return '';
  const units = ['B', 'KB', 'MB'];
  let i = 0, v = bytes;
  while (v >= 1024 && i < units.length - 1) { v /= 1024; i++; }
  return `${v.toFixed(v < 10 && i > 0 ? 1 : 0)} ${units[i]}`;
}

function iconFor(mime) {
  return (mime || '').startsWith('image/') ? ImageIcon : FileText;
}

function formatStorage(bytes) {
  if (!bytes) return '0 MB';
  if (bytes >= 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
  return `${Math.round(bytes / (1024 * 1024))} MB`;
}

function StorageUsageBar({ usage }) {
  if (!usage) return null;
  const pct = Math.min(100, usage.percentage || 0);
  const colorClass = pct >= 90 ? 'bg-red-500' : pct >= 70 ? 'bg-amber-500' : 'bg-emerald-500';
  return (
    <div className="w-full sm:w-64 mt-2" data-testid="storage-usage-bar">
      <div className="flex items-center justify-between text-[10px] text-muted-foreground mb-1">
        <span>Almacenamiento</span>
        <span>{formatStorage(usage.used_bytes)} de {formatStorage(usage.limit_bytes)}</span>
      </div>
      <div className="w-full h-1.5 bg-white/5 rounded-full overflow-hidden">
        <div className={`h-full rounded-full transition-all ${colorClass}`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

export default function FileLibraryPage() {
  const { user } = useAuth();
  const canManage = ADMIN_ROLES.includes(user?.role);
  const [files, setFiles] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(0);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('uploaded'); // 'uploaded' | 'inbound_whatsapp' — ver TICKET_MEDIA_ENTRANTE.md
  const changeTab = (value) => { setTab(value); setPage(1); };

  const [reuseFile, setReuseFile] = useState(null);
  const [reuseSearch, setReuseSearch] = useState('');
  const [reuseResults, setReuseResults] = useState([]);
  const [reuseSearching, setReuseSearching] = useState(false);
  const [reuseSendingId, setReuseSendingId] = useState(null);

  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadSelected, setUploadSelected] = useState(new Set());
  const [uploading, setUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const uploadInputRef = useRef(null);

  const [authFile, setAuthFile] = useState(null);
  const [authSelected, setAuthSelected] = useState(new Set());
  const [authSaving, setAuthSaving] = useState(false);

  const [team, setTeam] = useState([]);
  const vendors = team.filter(m => AUTHORIZABLE_ROLES.includes(m.role));

  const [historyFile, setHistoryFile] = useState(null);
  const [historyEntries, setHistoryEntries] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  const [auditOpen, setAuditOpen] = useState(false);

  const [storageUsage, setStorageUsage] = useState(null);

  const [renameFile, setRenameFile] = useState(null);
  const [renameValue, setRenameValue] = useState('');
  const [renameSaving, setRenameSaving] = useState(false);

  const canDelete = (f) => canManage || f.uploaded_by_user_id === user?.id;
  // Permisos POR PESTAÑA, no unión de roles: en "Subidos" solo admin-like; en "Recibidos" SOLO
  // el vendor/guard autorizado a ESE archivo — admin-like queda AFUERA a propósito (mismo
  // candado que ya aplica el backend en PATCH /file-library/{file_id}/rename).
  const canRename = (f) => (tab === 'uploaded' ? canManage : (f.authorized_vendor_ids || []).includes(user?.id));

  const load = useCallback(() => {
    setLoading(true);
    api.get('/file-library', { params: { page, limit: 25, search, source: tab } })
      .then(r => { setFiles(r.data.files); setTotal(r.data.total); setPages(r.data.pages); })
      .catch(() => toast.error('Error al cargar la Biblioteca'))
      .finally(() => setLoading(false));
  }, [page, search, tab]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!canManage) return;
    api.get('/team')
      .then(r => setTeam(Array.isArray(r.data) ? r.data : []))
      .catch(() => {});
  }, [canManage]);

  const loadStorageUsage = useCallback(() => {
    if (!canManage) return;
    api.get('/file-library/storage-usage')
      .then(r => setStorageUsage(r.data))
      .catch(() => {});
  }, [canManage]);

  useEffect(() => { loadStorageUsage(); }, [loadStorageUsage]);

  const handleDownload = async (f) => {
    try {
      const r = await api.get(`/files/${f.file_id}/download`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = f.original_name || 'archivo';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error('No se pudo descargar (puede haber expirado)');
    }
  };

  const handleDelete = async (f) => {
    if (!window.confirm(`Eliminar "${f.original_name}"? Esta accion no se puede deshacer.`)) return;
    try {
      await api.delete(`/files/${f.file_id}`);
      toast.success('Archivo eliminado');
      load();
      loadStorageUsage();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al eliminar');
    }
  };

  const openUpload = () => {
    setUploadFile(null);
    setUploadSelected(new Set());
    setUploadOpen(true);
  };

  const toggleUploadVendor = (id) => {
    setUploadSelected(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleUploadSelectAll = (selectAll) => {
    setUploadSelected(selectAll ? new Set(vendors.map(v => v.id)) : new Set());
  };

  const handleUploadDrop = (e) => {
    e.preventDefault();
    setDragActive(false);
    const f = e.dataTransfer?.files?.[0];
    if (f) setUploadFile(f);
  };

  const submitUpload = async () => {
    if (!uploadFile) { toast.error('Selecciona un archivo'); return; }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', uploadFile);
      formData.append('authorized_vendor_ids', JSON.stringify(Array.from(uploadSelected)));
      await api.post('/file-library', formData);
      toast.success('Archivo subido a la Biblioteca');
      setUploadOpen(false);
      load();
      loadStorageUsage();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo subir el archivo');
    } finally {
      setUploading(false);
    }
  };

  const openAuthorize = (f) => {
    setAuthFile(f);
    setAuthSelected(new Set(f.authorized_vendor_ids || []));
  };

  const toggleAuthVendor = (id) => {
    setAuthSelected(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleAuthSelectAll = (selectAll) => {
    setAuthSelected(selectAll ? new Set(vendors.map(v => v.id)) : new Set());
  };

  const submitAuthorize = async () => {
    const original = new Set(authFile.authorized_vendor_ids || []);
    const add_vendor_ids = Array.from(authSelected).filter(id => !original.has(id));
    const remove_vendor_ids = Array.from(original).filter(id => !authSelected.has(id));
    if (add_vendor_ids.length === 0 && remove_vendor_ids.length === 0) { setAuthFile(null); return; }
    setAuthSaving(true);
    try {
      await api.patch(`/file-library/${authFile.file_id}/authorize`, { add_vendor_ids, remove_vendor_ids });
      toast.success('Autorizaciones actualizadas');
      setAuthFile(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo actualizar');
    } finally {
      setAuthSaving(false);
    }
  };

  const openRename = (f) => {
    const dot = f.original_name.lastIndexOf('.');
    setRenameFile(f);
    setRenameValue(dot > 0 ? f.original_name.slice(0, dot) : f.original_name);
  };

  const submitRename = async () => {
    const base = renameValue.trim();
    if (!base) { toast.error('El nombre no puede estar vacío'); return; }
    setRenameSaving(true);
    try {
      const r = await api.patch(`/file-library/${renameFile.file_id}/rename`, { name: base });
      toast.success('Archivo renombrado');
      setRenameFile(null);
      setFiles(prev => prev.map(f => f.file_id === renameFile.file_id ? { ...f, original_name: r.data.original_name } : f));
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo renombrar');
    } finally {
      setRenameSaving(false);
    }
  };

  const openSendHistory = async (f) => {
    setHistoryFile(f);
    setHistoryLoading(true);
    try {
      const r = await api.get(`/file-library/${f.file_id}/send-history`);
      setHistoryEntries(r.data.send_history || []);
    } catch {
      toast.error('No se pudo cargar el historial de envíos');
      setHistoryEntries([]);
    } finally {
      setHistoryLoading(false);
    }
  };

  const openReuse = (f) => {
    setReuseFile(f);
    setReuseSearch('');
    setReuseResults([]);
  };

  const searchReuseConversations = async () => {
    if (!reuseSearch.trim()) return;
    setReuseSearching(true);
    try {
      const r = await api.get('/ai-agent/conversations', { params: { search: reuseSearch.trim() } });
      const activos = (r.data || []).filter(c => c.status === 'active' || c.status === 'taken_over');
      setReuseResults(activos);
    } catch {
      toast.error('Error al buscar conversaciones');
    } finally {
      setReuseSearching(false);
    }
  };

  const confirmReuse = async (conv) => {
    setReuseSendingId(conv.id);
    try {
      const r = await api.post(`/file-library/${reuseFile.file_id}/send-to-conversation/${conv.id}`);
      const { status, message } = r.data;
      if (status === 'pending_window_closed') {
        toast.info(message || 'Ventana cerrada — se envió un recordatorio, el archivo se entregará cuando el lead responda.');
      } else if (status === 'no_template_configured' || status === 'template_send_failed') {
        toast.warning(message || 'No se pudo enviar: ventana de 24h cerrada.');
      } else {
        toast.success(`Archivo reenviado a ${conv.lead_name || conv.phone}`);
      }
      setReuseFile(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo reutilizar el archivo');
    } finally {
      setReuseSendingId(null);
    }
  };

  return (
    <div data-testid="file-library-page">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h2 className="font-heading text-2xl font-bold text-white flex items-center gap-2">
            <FolderOpen className="w-6 h-6 text-primary" /> Biblioteca
          </h2>
          <p className="text-muted-foreground text-sm mt-1">
            {total} archivo{total !== 1 ? 's' : ''} · Se borran automaticamente a los 30 dias
          </p>
          {canManage && <StorageUsageBar usage={storageUsage} />}
        </div>
        <div className="flex items-center gap-2 w-full sm:w-auto">
          <div className="relative w-full sm:w-72">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Buscar por nombre..."
              value={search}
              onChange={e => { setSearch(e.target.value); setPage(1); }}
              className="pl-9 bg-black/20 border-white/10"
              data-testid="file-library-search"
            />
          </div>
          {canManage && tab === 'uploaded' && (
            <Button onClick={() => setAuditOpen(true)} variant="outline" className="border-white/10 shrink-0" data-testid="audit-log-btn">
              <History className="w-4 h-4 mr-1.5" /> Historial de actividad
            </Button>
          )}
          {canManage && tab === 'uploaded' && (
            <Button onClick={openUpload} className="bg-primary hover:bg-primary/90 text-black shrink-0" data-testid="upload-file-btn">
              <Upload className="w-4 h-4 mr-1.5" /> Subir archivo
            </Button>
          )}
        </div>
      </div>

      <Tabs value={tab} onValueChange={changeTab} className="mb-4">
        <TabsList className="bg-black/20 border border-white/10">
          <TabsTrigger value="uploaded" data-testid="tab-uploaded">
            <Upload className="w-4 h-4 mr-1.5" /> Subidos
          </TabsTrigger>
          <TabsTrigger value="inbound_whatsapp" data-testid="tab-inbound">
            <Inbox className="w-4 h-4 mr-1.5" /> Recibidos
          </TabsTrigger>
        </TabsList>
      </Tabs>

      {loading ? (
        <div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" /></div>
      ) : files.length === 0 ? (
        <Card className="bg-card/50 backdrop-blur-md border-white/5">
          <CardContent className="py-16 text-center">
            <FolderOpen className="w-12 h-12 mx-auto text-muted-foreground/30 mb-4" />
            <p className="text-white font-semibold mb-2">Sin archivos todavia</p>
            <p className="text-muted-foreground text-sm max-w-md mx-auto">
              {canManage
                ? 'Sube un archivo y autoriza a los vendedores que lo pueden enviar a sus leads.'
                : 'Aqui apareceran los archivos que el administrador te autorice a usar.'}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-white/5 bg-card/50 backdrop-blur-md">
          <table className="w-full text-xs min-w-[820px]">
            <thead>
              <tr className="border-b border-white/10 text-muted-foreground text-[10px] uppercase tracking-wider">
                <th className="text-left py-2.5 px-3 font-medium">Archivo</th>
                <th className="text-left py-2.5 px-3 font-medium">Tipo</th>
                <th className="text-left py-2.5 px-3 font-medium">Tamaño</th>
                <th className="text-left py-2.5 px-3 font-medium">{tab === 'inbound_whatsapp' ? 'Recibido de' : 'Subido por'}</th>
                <th className="text-left py-2.5 px-3 font-medium">Subido</th>
                <th className="text-left py-2.5 px-3 font-medium">Expira</th>
                <th className="text-left py-2.5 px-3 font-medium">{tab === 'inbound_whatsapp' ? 'Enviado a' : 'Envíos'}</th>
                <th className="text-right py-2.5 px-3 font-medium sticky right-0 bg-card border-l border-white/10">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {files.map(f => {
                const Icon = iconFor(f.mime_type);
                return (
                  <tr key={f.file_id} className="border-b border-white/5 last:border-0 hover:bg-white/[0.02]" data-testid={`file-library-item-${f.file_id}`}>
                    <td className="py-2.5 px-3">
                      <div className="flex items-center gap-2 min-w-0">
                        <div className="w-7 h-7 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                          <Icon className="w-3.5 h-3.5 text-primary" />
                        </div>
                        <span className="text-white font-medium truncate max-w-[220px]" title={f.original_name}>{f.original_name}</span>
                      </div>
                    </td>
                    <td className="py-2.5 px-3 text-muted-foreground">{fileTypeLabel(f.mime_type, f.original_name)}</td>
                    <td className="py-2.5 px-3 text-muted-foreground whitespace-nowrap">{formatFileSize(f.size_bytes)}</td>
                    <td className="py-2.5 px-3 text-muted-foreground truncate max-w-[140px]">{tab === 'inbound_whatsapp' ? (f.received_from_lead_name || '-') : (f.uploaded_by_name || '-')}</td>
                    <td className="py-2.5 px-3 text-muted-foreground whitespace-nowrap">{new Date(f.uploaded_at).toLocaleDateString('es-MX')}</td>
                    <td className="py-2.5 px-3 text-muted-foreground whitespace-nowrap">{daysUntil(f.expires_at)}</td>
                    <td className="py-2.5 px-3">
                      {tab === 'inbound_whatsapp' ? (
                        <span className="text-muted-foreground truncate max-w-[140px] block">{f.assigned_vendor_name || '-'}</span>
                      ) : (
                        <button
                          onClick={() => openSendHistory(f)}
                          className="flex items-center gap-1 text-emerald-400 hover:text-emerald-300 whitespace-nowrap"
                          data-testid={`file-library-history-${f.file_id}`}
                        >
                          <Send className="w-3 h-3" /> {f.usage_count || 0}
                        </button>
                      )}
                    </td>
                    <td className="py-2.5 px-3 sticky right-0 bg-card border-l border-white/10">
                      <div className="flex items-center justify-end gap-0.5">
                        <Button variant="ghost" size="sm" onClick={() => handleDownload(f)} className="h-7 w-7 p-0 text-muted-foreground hover:text-primary hover:bg-primary/10" data-testid={`file-library-download-${f.file_id}`}>
                          <Download className="w-3.5 h-3.5" />
                        </Button>
                        {canRename(f) && (
                          <Button variant="ghost" size="sm" onClick={() => openRename(f)} className="h-7 w-7 p-0 text-muted-foreground hover:text-primary hover:bg-primary/10" title="Renombrar" data-testid={`file-library-rename-${f.file_id}`}>
                            <Pencil className="w-3.5 h-3.5" />
                          </Button>
                        )}
                        {canManage && tab === 'uploaded' && (
                          <Button variant="ghost" size="sm" onClick={() => openAuthorize(f)} className="h-7 w-7 p-0 text-muted-foreground hover:text-primary hover:bg-primary/10" title="Editar autorizados" data-testid={`file-library-authorize-${f.file_id}`}>
                            <Users className="w-3.5 h-3.5" />
                          </Button>
                        )}
                        {tab === 'uploaded' && (
                          <Button variant="ghost" size="sm" onClick={() => openReuse(f)} className="h-7 w-7 p-0 text-muted-foreground hover:text-emerald-400 hover:bg-emerald-500/10" title="Reutilizar en otra conversación" data-testid={`file-library-reuse-${f.file_id}`}>
                            <Repeat className="w-3.5 h-3.5" />
                          </Button>
                        )}
                        {canDelete(f) && (
                          <Button variant="ghost" size="sm" onClick={() => handleDelete(f)} className="h-7 w-7 p-0 text-muted-foreground hover:text-destructive hover:bg-destructive/10" data-testid={`file-library-delete-${f.file_id}`}>
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {pages > 1 && (
        <div className="flex items-center justify-center gap-3 mt-6">
          <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)} className="border-white/10">
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <span className="text-xs text-muted-foreground">Página {page} de {pages}</span>
          <Button variant="outline" size="sm" disabled={page >= pages} onClick={() => setPage(p => p + 1)} className="border-white/10">
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>
      )}

      <Dialog open={!!reuseFile} onOpenChange={(open) => !open && setReuseFile(null)}>
        <DialogContent className="max-w-md bg-[#1F2833] border-white/10" data-testid="reuse-file-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-white flex items-center gap-2">
              <Repeat className="w-5 h-5 text-emerald-400" /> Reutilizar archivo
            </DialogTitle>
            <DialogDescription className="text-muted-foreground text-xs">
              Busca la conversación destino para reenviar <strong>{reuseFile?.original_name}</strong> por
              WhatsApp, sin volver a subirlo.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="flex gap-2">
              <Input
                placeholder="Buscar por nombre o teléfono..."
                value={reuseSearch}
                onChange={e => setReuseSearch(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && searchReuseConversations()}
                className="bg-black/20 border-white/10"
                data-testid="reuse-search-input"
              />
              <Button onClick={searchReuseConversations} disabled={reuseSearching || !reuseSearch.trim()} className="bg-primary hover:bg-primary/90 text-black shrink-0" data-testid="reuse-search-btn">
                {reuseSearching ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
              </Button>
            </div>
            <div className="max-h-64 overflow-y-auto space-y-1.5">
              {reuseResults.length === 0 ? (
                <p className="text-xs text-muted-foreground text-center py-4">
                  {reuseSearching ? 'Buscando...' : 'Busca una conversación activa para reenviar el archivo.'}
                </p>
              ) : reuseResults.map(c => (
                <button
                  key={c.id}
                  onClick={() => confirmReuse(c)}
                  disabled={reuseSendingId === c.id}
                  className="w-full flex items-center gap-2.5 p-2.5 rounded-lg bg-black/20 border border-white/5 hover:border-emerald-500/30 hover:bg-emerald-500/5 text-left transition-colors disabled:opacity-50"
                  data-testid={`reuse-conv-option-${c.id}`}
                >
                  <div className="w-8 h-8 rounded-full bg-white/5 flex items-center justify-center shrink-0">
                    <User className="w-4 h-4 text-muted-foreground" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-xs font-semibold text-white truncate">{c.lead_name || 'Sin nombre'}</p>
                    <p className="text-[10px] text-muted-foreground">{c.phone}</p>
                  </div>
                  {reuseSendingId === c.id ? (
                    <Loader2 className="w-4 h-4 animate-spin text-emerald-400 shrink-0" />
                  ) : (
                    <Repeat className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                  )}
                </button>
              ))}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={uploadOpen} onOpenChange={setUploadOpen}>
        <DialogContent className="max-w-md bg-[#1F2833] border-white/10" data-testid="upload-file-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-white flex items-center gap-2">
              <Upload className="w-5 h-5 text-primary" /> Subir archivo a la Biblioteca
            </DialogTitle>
            <DialogDescription className="text-muted-foreground text-xs">
              Elige el archivo y marca quiénes lo pueden usar. Máx. 20MB.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div
              onClick={() => uploadInputRef.current?.click()}
              onDrop={handleUploadDrop}
              onDragOver={(e) => { e.preventDefault(); setDragActive(true); }}
              onDragLeave={() => setDragActive(false)}
              className={`rounded-xl border-2 border-dashed p-6 text-center cursor-pointer transition ${
                dragActive ? 'border-primary bg-primary/5' : 'border-white/20 bg-black/20 hover:border-primary/40'
              }`}
              data-testid="upload-dropzone"
            >
              <input
                ref={uploadInputRef}
                type="file"
                onChange={e => setUploadFile(e.target.files?.[0] || null)}
                className="hidden"
                data-testid="upload-file-input"
              />
              {uploadFile ? (
                <UploadFilePreview file={uploadFile} onClear={() => setUploadFile(null)} />
              ) : (
                <div className="space-y-1.5">
                  <Upload className="w-8 h-8 mx-auto text-muted-foreground" />
                  <p className="text-sm text-white">Arrastra tu archivo aquí o haz clic para seleccionar</p>
                  <p className="text-[11px] text-muted-foreground">PDF, imagen, Excel, Word — máx. 20MB</p>
                </div>
              )}
            </div>
            <VendorChecklist
              vendors={vendors}
              selected={uploadSelected}
              onToggle={toggleUploadVendor}
              onToggleAll={toggleUploadSelectAll}
            />
            <Button
              onClick={submitUpload}
              disabled={uploading || !uploadFile}
              className="w-full bg-primary hover:bg-primary/90 text-black"
              data-testid="upload-file-submit"
            >
              {uploading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Upload className="w-4 h-4 mr-2" />}
              Subir
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!authFile} onOpenChange={(open) => !open && setAuthFile(null)}>
        <DialogContent className="max-w-md bg-[#1F2833] border-white/10" data-testid="authorize-file-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-white flex items-center gap-2">
              <Users className="w-5 h-5 text-primary" /> Autorizados
            </DialogTitle>
            <DialogDescription className="text-muted-foreground text-xs">
              Quién puede usar <strong>{authFile?.original_name}</strong> para enviarlo a sus leads.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <VendorChecklist
              vendors={vendors}
              selected={authSelected}
              onToggle={toggleAuthVendor}
              onToggleAll={toggleAuthSelectAll}
            />
            <Button
              onClick={submitAuthorize}
              disabled={authSaving}
              className="w-full bg-primary hover:bg-primary/90 text-black"
              data-testid="authorize-file-submit"
            >
              {authSaving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Guardar
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!renameFile} onOpenChange={(open) => !open && setRenameFile(null)}>
        <DialogContent className="max-w-sm bg-[#1F2833] border-white/10" data-testid="rename-file-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-white flex items-center gap-2">
              <Pencil className="w-5 h-5 text-primary" /> Renombrar archivo
            </DialogTitle>
            <DialogDescription className="text-muted-foreground text-xs">
              La extensión no se puede cambiar.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="flex items-center gap-1.5">
              <Input
                value={renameValue}
                onChange={e => setRenameValue(e.target.value)}
                className="bg-black/20 border-white/10"
                autoFocus
                data-testid="rename-file-input"
              />
              <span className="text-muted-foreground text-sm whitespace-nowrap">
                {renameFile && renameFile.original_name.lastIndexOf('.') > 0
                  ? renameFile.original_name.slice(renameFile.original_name.lastIndexOf('.'))
                  : ''}
              </span>
            </div>
            <Button
              onClick={submitRename}
              disabled={renameSaving}
              className="w-full bg-primary hover:bg-primary/90 text-black"
              data-testid="rename-file-submit"
            >
              {renameSaving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Guardar
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!historyFile} onOpenChange={(open) => { if (!open) { setHistoryFile(null); setHistoryEntries([]); } }}>
        <DialogContent className="max-w-md max-h-[80vh] overflow-y-auto bg-[#1F2833] border-white/10" data-testid="send-history-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-white flex items-center gap-2">
              <Send className="w-5 h-5 text-emerald-400" /> Envíos de {historyFile?.original_name}
            </DialogTitle>
            <DialogDescription className="text-muted-foreground text-xs">
              Leads a los que se envió este archivo, más reciente primero.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-1.5">
            {historyLoading ? (
              <div className="flex items-center justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-muted-foreground" /></div>
            ) : historyEntries.length === 0 ? (
              <p className="text-xs text-muted-foreground text-center py-6">Este archivo aún no se ha enviado a nadie.</p>
            ) : historyEntries.map((h, i) => (
              <div key={i} className="flex items-center gap-2.5 p-2.5 rounded-lg bg-black/20 border border-white/5" data-testid="send-history-entry">
                <div className="w-8 h-8 rounded-full bg-white/5 flex items-center justify-center shrink-0">
                  <User className="w-4 h-4 text-muted-foreground" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-xs font-semibold text-white truncate">{h.lead_name || 'Sin nombre'}</p>
                  <p className="text-[10px] text-muted-foreground">
                    {new Date(h.sent_at).toLocaleString('es-MX')} · por {h.sent_by_user_name || '-'}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      <AuditLogModal open={auditOpen} onClose={() => setAuditOpen(false)} team={team} />
    </div>
  );
}
